import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Form, Modal, Spinner } from "react-bootstrap";
import {
  TbPlus, TbX, TbCheck, TbClock, TbSearch, TbUserPlus, TbUser,
} from "react-icons/tb";
import bmsService, {
  BmsCalendarEvent, BmsCalendarResource, BmsCustomer,
  BmsServiceItem, BmsUser,
} from "../../features/bms/bmsService";
import { showError, showSuccess } from "../../utils/bmsToast";

const DEFAULT_TZ = "Europe/Istanbul";
const STATUS_OPTIONS = ["confirmed", "pending", "cancelled", "completed"] as const;

const statusInfo = (s: string, t: (k: string) => string) => {
  switch (s) {
    case "confirmed": return { cls: "confirmed", label: t("bms_confirmed") };
    case "pending": return { cls: "pending", label: t("bms_pending") };
    case "cancelled": return { cls: "cancelled", label: t("bms_cancelled") };
    case "completed": return { cls: "completed", label: t("bms_completed") };
    default: return { cls: "", label: s };
  }
};

type SlotStatus = "available" | "booked" | "off";

interface Slot {
  startMin: number;
  endMin: number;
  label: string;
  status: SlotStatus;
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function toIsoDate(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function todayIso(): string {
  return toIsoDate(new Date());
}

function minutesToHHMM(m: number): string {
  return `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}`;
}

function parseTimeToMinutes(val: string | null | undefined): number | null {
  if (!val) return null;
  const parts = val.split(":").map(Number);
  if (parts.length < 2 || isNaN(parts[0]) || isNaN(parts[1])) return null;
  return parts[0] * 60 + parts[1];
}

function eventMinutesForDate(ts: string, isoDate: string): number | null {
  if (!ts) return null;
  const normalized = ts.replace(" ", "T");
  if (!normalized.startsWith(isoDate)) return null;
  const timePart = normalized.slice(11, 16);
  return parseTimeToMinutes(timePart);
}

function formatTimeRange(startMin: number, endMin: number): string {
  return `${minutesToHHMM(startMin)} – ${minutesToHHMM(endMin)}`;
}

export interface CalendarBookingContext {
  date: Date;
  hour?: number; // fractional hour e.g. 14.5 = 14:30
  expertId?: number;
}

interface Props {
  show: boolean;
  onHide: () => void;
  chatbotId: number | null;
  context: CalendarBookingContext | null;
  onCreated: () => void;
}

const CreateBookingFromCalendarModal: React.FC<Props> = ({
  show, onHide, chatbotId, context, onCreated,
}) => {
  const { t } = useTranslation();

  // Reference data
  const [users, setUsers] = useState<BmsUser[]>([]);
  const [services, setServices] = useState<BmsServiceItem[]>([]);
  const [customers, setCustomers] = useState<BmsCustomer[]>([]);
  const [loadingRefs, setLoadingRefs] = useState(false);

  // Form state
  const [resourceUserId, setResourceUserId] = useState<number>(0);
  const [serviceId, setServiceId] = useState<number>(0);
  const [bookingDate, setBookingDate] = useState<string>(todayIso());
  const [selectedStartMin, setSelectedStartMin] = useState<number | null>(null);
  const [startAt, setStartAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const [bookingStatus, setBookingStatus] = useState<string>("confirmed");

  // Customer mode
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const [serviceSearch, setServiceSearch] = useState("");
  const [showServiceDropdown, setShowServiceDropdown] = useState(false);
  const serviceSearchRef = useRef<HTMLDivElement>(null);

  // New customer fields
  const [customerName, setCustomerName] = useState("");
  const [customerSurname, setCustomerSurname] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");

  // Slot data
  const [calendarResource, setCalendarResource] = useState<BmsCalendarResource | null>(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [saving, setSaving] = useState(false);

  const activeUsers = useMemo(() => users.filter((u) => u.status === "active"), [users]);
  const activeServices = useMemo(() => services.filter((s) => !!s.is_active), [services]);

  // Load reference data
  useEffect(() => {
    if (!show || !chatbotId) return;
    const load = async () => {
      setLoadingRefs(true);
      try {
        const [uRes, sRes, cRes] = await Promise.all([
          bmsService.listUsers(chatbotId, { limit: 200 }),
          bmsService.listServices(chatbotId, { limit: 200 }),
          bmsService.listCustomers(chatbotId, { limit: 200 }),
        ]);
        const uList = (uRes.data || []).filter((u) => u.status === "active");
        const sList = (sRes.data || []).filter((s) => !!s.is_active);
        setUsers(uRes.data || []);
        setServices(sRes.data || []);
        setCustomers(cRes.data || []);

        // Pre-select from context
        const expertId = context?.expertId;
        if (expertId && uList.some((u) => u.id === expertId)) {
          setResourceUserId(expertId);
        } else if (uList.length) {
          setResourceUserId(uList[0].id);
        }
        if (sList.length) setServiceId(sList[0].id);
      } catch (e: any) {
        showError(e?.response?.data?.detail || e?.message || "Error");
      } finally {
        setLoadingRefs(false);
      }
    };
    load();
  }, [show, chatbotId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Set date from context
  useEffect(() => {
    if (!show || !context) return;
    setBookingDate(toIsoDate(context.date));
    setSelectedStartMin(null);
    setStartAt("");
    setEndAt("");
    setIsNewCustomer(false);
    setSelectedCustomerId(null);
    setCustomerSearch("");
    setCustomerName("");
    setCustomerSurname("");
    setCustomerPhone("");
    setCustomerEmail("");
    setServiceSearch("");
    setShowServiceDropdown(false);
    setBookingStatus("confirmed");
  }, [show, context]);

  // Load slots when expert/date changes
  useEffect(() => {
    if (!show || !chatbotId || !resourceUserId || !bookingDate) {
      setCalendarResource(null);
      return;
    }
    const loadSlots = async () => {
      setLoadingSlots(true);
      try {
        const res = await bmsService.getCalendar(chatbotId, {
          start_at: `${bookingDate}T00:00:00`,
          end_at: `${bookingDate}T23:59:59`,
        });
        const resource = (res.resources || []).find(
          (r) => r.resource.resource_user_id === resourceUserId
        );
        setCalendarResource(resource || null);
      } catch (e: any) {
        showError(e?.response?.data?.detail || e?.message || "Error");
        setCalendarResource(null);
      } finally {
        setLoadingSlots(false);
      }
    };
    loadSlots();
    setSelectedStartMin(null);
  }, [show, chatbotId, resourceUserId, bookingDate]);

  // Auto-select slot if context has hour
  useEffect(() => {
    if (!context?.hour || !calendarResource || selectedStartMin !== null) return;
    const targetMin = Math.floor(context.hour) * 60;
    // Try to find the closest available slot
    const slots = computedSlots;
    const avail = slots.filter((s) => s.status === "available");
    if (avail.length === 0) return;
    let best = avail[0];
    let bestDist = Math.abs(best.startMin - targetMin);
    for (const s of avail) {
      const d = Math.abs(s.startMin - targetMin);
      if (d < bestDist) { best = s; bestDist = d; }
    }
    handleSelectSlot(best);
  }, [calendarResource]); // eslint-disable-line react-hooks/exhaustive-deps

  const slotDuration = useMemo(() => {
    const svc = activeServices.find((s) => s.id === serviceId);
    return svc?.duration_minutes && svc.duration_minutes > 0 ? svc.duration_minutes : 30;
  }, [activeServices, serviceId]);

  const computedSlots = useMemo<Slot[]>(() => {
    if (!calendarResource) return [];
    const dayOfWeek = new Date(`${bookingDate}T00:00:00`).getDay();
    const applicable = (calendarResource.availability || []).filter((a) =>
      Array.isArray(a.days) && a.days.includes(dayOfWeek)
    );
    const workingRanges: Array<[number, number]> = [];
    const breakRanges: Array<[number, number]> = [];
    for (const a of applicable) {
      const ws = parseTimeToMinutes(a.start_at);
      const we = parseTimeToMinutes(a.end_at);
      if (ws !== null && we !== null && we > ws) workingRanges.push([ws, we]);
      const bs = parseTimeToMinutes(a.break_start_at ?? undefined);
      const be = parseTimeToMinutes(a.break_end_at ?? undefined);
      if (bs !== null && be !== null && be > bs) breakRanges.push([bs, be]);
    }
    if (workingRanges.length === 0) return [];
    const minStart = Math.min(...workingRanges.map((r) => r[0]));
    const maxEnd = Math.max(...workingRanges.map((r) => r[1]));
    const alignedStart = Math.floor(minStart / slotDuration) * slotDuration;
    const alignedEnd = Math.ceil(maxEnd / slotDuration) * slotDuration;
    const bookedRanges: Array<[number, number]> = [];
    for (const ev of (calendarResource.events || []) as BmsCalendarEvent[]) {
      if (ev.status === "cancelled") continue;
      const s = eventMinutesForDate(ev.start_at, bookingDate);
      const e = eventMinutesForDate(ev.end_at, bookingDate);
      if (s !== null && e !== null && e > s) bookedRanges.push([s, e]);
    }
    const overlapsAny = (a: number, b: number, ranges: Array<[number, number]>) =>
      ranges.some(([rs, re]) => a < re && b > rs);
    const containedInAny = (a: number, b: number, ranges: Array<[number, number]>) =>
      ranges.some(([rs, re]) => a >= rs && b <= re);
    const result: Slot[] = [];
    for (let m = alignedStart; m + slotDuration <= alignedEnd; m += slotDuration) {
      const endM = m + slotDuration;
      let status: SlotStatus;
      if (!containedInAny(m, endM, workingRanges) || overlapsAny(m, endM, breakRanges)) {
        status = "off";
      } else if (overlapsAny(m, endM, bookedRanges)) {
        status = "booked";
      } else {
        status = "available";
      }
      result.push({ startMin: m, endMin: endM, label: minutesToHHMM(m), status });
    }
    return result;
  }, [calendarResource, bookingDate, slotDuration]);

  const handleSelectSlot = (slot: Slot) => {
    if (slot.status !== "available") return;
    setSelectedStartMin(slot.startMin);
    setStartAt(`${bookingDate}T${minutesToHHMM(slot.startMin)}`);
    setEndAt(`${bookingDate}T${minutesToHHMM(slot.endMin)}`);
  };

  // Customer search
  const filteredCustomers = useMemo(() => {
    if (!customerSearch.trim()) return customers;
    const q = customerSearch.toLowerCase();
    return customers.filter((c) =>
      `${c.name} ${c.surname}`.toLowerCase().includes(q) ||
      c.phone?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q)
    );
  }, [customers, customerSearch]);

  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === selectedCustomerId) || null,
    [customers, selectedCustomerId]
  );

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
      if (serviceSearchRef.current && !serviceSearchRef.current.contains(e.target as Node)) {
        setShowServiceDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleCreate = async () => {
    if (!chatbotId) return;

    let name = "", surname = "", phone = "", email = "";
    if (isNewCustomer) {
      name = customerName.trim();
      surname = customerSurname.trim();
      phone = customerPhone.trim();
      email = customerEmail.trim();
    } else if (selectedCustomer) {
      name = selectedCustomer.name;
      surname = selectedCustomer.surname;
      phone = selectedCustomer.phone;
      email = selectedCustomer.email || "";
    }

    if (!name || !phone) {
      showError(t("bms_invalid_customer"));
      return;
    }
    if (!serviceId) {
      showError(t("bms_select_service_booking"));
      return;
    }
    if (!startAt || !endAt) {
      showError(t("bms_invalid_datetime"));
      return;
    }

    setSaving(true);
    try {
      const res = await bmsService.createBooking(chatbotId, {
        resource_user_id: resourceUserId,
        service_id: serviceId,
        customer_name: name,
        customer_surname: surname,
        customer_phone: phone,
        customer_email: email || undefined,
        start_at: startAt.replace("T", " "),
        end_at: endAt.replace("T", " "),
        timezone: DEFAULT_TZ,
        status: bookingStatus,
      });
      showSuccess(`${t("bms_booking_created")} ${res.booking_number}`);
      onHide();
      onCreated();
    } catch (e: any) {
      showError(e?.response?.data?.detail || e?.message || "Error");
    } finally {
      setSaving(false);
    }
  };

  const hasAvailability = computedSlots.length > 0;
  const canSave = selectedStartMin !== null && (isNewCustomer ? (customerName.trim() && customerPhone.trim()) : !!selectedCustomerId);

  return (
    <Modal show={show} onHide={onHide} className="bms-modal" centered size="lg">
      <Modal.Header closeButton className="bms-modal-header">
        <Modal.Title className="d-flex align-items-center gap-2">
          <TbPlus /> {t("bms_cal_new_booking")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loadingRefs ? (
          <div className="d-flex justify-content-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <div className="row g-3">
            {/* Expert & Service */}
            <div className="col-md-6">
              <Form.Label className="bms-form-label">{t("bms_select_expert_booking")}</Form.Label>
              <Form.Select value={resourceUserId} onChange={(e) => setResourceUserId(Number(e.target.value))}>
                {activeUsers.map((u) => (
                  <option key={u.id} value={u.id}>{u.display_name}</option>
                ))}
              </Form.Select>
            </div>
            <div className="col-md-6">
              <Form.Label className="bms-form-label">{t("bms_select_service_booking")}</Form.Label>
              <div className="bms-customer-search-wrap" ref={serviceSearchRef}>
                <div className="bms-customer-search-input">
                  <TbSearch className="bms-cs-icon" />
                  <input
                    type="text"
                    className="bms-cs-field"
                    placeholder={t("bms_select_service_booking")}
                    value={serviceId ? (() => {
                      const s = activeServices.find((x) => x.id === serviceId);
                      return s ? `${s.name} (${s.duration_minutes} ${t("bms_minutes_short")})` : serviceSearch;
                    })() : serviceSearch}
                    onChange={(e) => {
                      setServiceSearch(e.target.value);
                      setServiceId(0);
                      setShowServiceDropdown(true);
                    }}
                    onFocus={() => setShowServiceDropdown(true)}
                  />
                  {serviceId > 0 && (
                    <button type="button" className="bms-cs-clear" onClick={() => { setServiceId(0); setServiceSearch(""); setShowServiceDropdown(true); }}>
                      <TbX size={14} />
                    </button>
                  )}
                </div>
                {showServiceDropdown && !serviceId && (
                  <div className="bms-customer-dropdown">
                    {(() => {
                      const q = serviceSearch.toLowerCase().trim();
                      const filtered = q ? activeServices.filter((s) => s.name.toLowerCase().includes(q)) : activeServices;
                      return filtered.length === 0 ? (
                        <div className="bms-cd-empty">{t("bms_no_data")}</div>
                      ) : (
                        filtered.slice(0, 8).map((s) => (
                          <button
                            key={s.id}
                            type="button"
                            className="bms-cd-item"
                            onClick={() => {
                              setServiceId(s.id);
                              setServiceSearch("");
                              setShowServiceDropdown(false);
                            }}
                          >
                            <div className="bms-cd-info">
                              <div className="bms-cd-name">{s.name}</div>
                              <div className="bms-cd-meta">{s.duration_minutes} {t("bms_minutes_short")}</div>
                            </div>
                          </button>
                        ))
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>

            {/* Date & Slots */}
            <div className="col-md-4">
              <Form.Label className="bms-form-label">{t("bms_booking_date")}</Form.Label>
              <Form.Control type="date" value={bookingDate} onChange={(e) => setBookingDate(e.target.value)} />
            </div>
            <div className="col-md-8">
              <Form.Label className="bms-form-label d-flex align-items-center justify-content-between">
                <span>{t("bms_select_slot")} ({slotDuration} {t("bms_minutes_short")})</span>
                {selectedStartMin !== null && (
                  <span className="bms-slot-selected-label">
                    <TbClock size={12} />{" "}
                    {formatTimeRange(selectedStartMin, selectedStartMin + slotDuration)}
                  </span>
                )}
              </Form.Label>
              <div className="bms-slot-legend">
                <span><i className="bms-slot-dot available" /> {t("bms_slot_available")}</span>
                <span><i className="bms-slot-dot booked" /> {t("bms_slot_booked")}</span>
                <span><i className="bms-slot-dot off" /> {t("bms_slot_off")}</span>
              </div>
              <div className="bms-slot-grid">
                {loadingSlots ? (
                  <div className="bms-slot-loading"><Spinner size="sm" animation="border" variant="primary" /></div>
                ) : computedSlots.length === 0 ? (
                  <div className="bms-slot-empty">
                    {hasAvailability ? t("bms_no_data") : t("bms_slot_no_availability")}
                  </div>
                ) : (
                  computedSlots.map((slot) => (
                    <button
                      key={slot.startMin}
                      type="button"
                      className={`bms-slot-pill ${slot.status}${selectedStartMin === slot.startMin ? " selected" : ""}`}
                      onClick={() => handleSelectSlot(slot)}
                      disabled={slot.status !== "available"}
                    >
                      {slot.label}
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Status Buttons */}
            <div className="col-12">
              <Form.Label className="bms-form-label">{t("bms_status")}</Form.Label>
              <div className="bms-status-btn-group">
                {STATUS_OPTIONS.map((s) => {
                  const info = statusInfo(s, t);
                  return (
                    <button key={s} type="button" className={`bms-status-btn ${info.cls}${bookingStatus === s ? " active" : ""}`} onClick={() => setBookingStatus(s)}>
                      <span className="bms-status-dot" /> {info.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Customer Section */}
            <div className="col-12">
              <div className="bms-customer-section">
                <div className="bms-customer-header">
                  <Form.Label className="bms-form-label mb-0">{t("bms_customer_select_label")}</Form.Label>
                  <label className="bms-new-customer-toggle">
                    <input
                      type="checkbox"
                      checked={isNewCustomer}
                      onChange={(e) => {
                        setIsNewCustomer(e.target.checked);
                        if (e.target.checked) {
                          setSelectedCustomerId(null);
                          setCustomerSearch("");
                        } else {
                          setCustomerName("");
                          setCustomerSurname("");
                          setCustomerPhone("");
                          setCustomerEmail("");
                        }
                      }}
                    />
                    <TbUserPlus size={14} />
                    <span>{t("bms_new_customer")}</span>
                  </label>
                </div>

                {!isNewCustomer ? (
                  /* Existing Customer Search */
                  <div className="bms-customer-search-wrap" ref={searchRef}>
                    <div className="bms-customer-search-input">
                      <TbSearch className="bms-cs-icon" />
                      <input
                        type="text"
                        className="bms-cs-field"
                        placeholder={t("bms_customer_search_placeholder")}
                        value={selectedCustomer ? `${selectedCustomer.name} ${selectedCustomer.surname} (${selectedCustomer.phone})` : customerSearch}
                        onChange={(e) => {
                          setCustomerSearch(e.target.value);
                          setSelectedCustomerId(null);
                          setShowDropdown(true);
                        }}
                        onFocus={() => setShowDropdown(true)}
                      />
                      {selectedCustomerId && (
                        <button
                          type="button"
                          className="bms-cs-clear"
                          onClick={() => { setSelectedCustomerId(null); setCustomerSearch(""); }}
                        >
                          <TbX size={14} />
                        </button>
                      )}
                    </div>
                    {showDropdown && !selectedCustomerId && (
                      <div className="bms-customer-dropdown">
                        {filteredCustomers.length === 0 ? (
                          <div className="bms-cd-empty">{t("bms_customer_no_results")}</div>
                        ) : (
                          filteredCustomers.slice(0, 8).map((c) => (
                            <button
                              key={c.id}
                              type="button"
                              className="bms-cd-item"
                              onClick={() => {
                                setSelectedCustomerId(c.id);
                                setCustomerSearch("");
                                setShowDropdown(false);
                              }}
                            >
                              <div className="bms-cd-avatar"><TbUser size={14} /></div>
                              <div className="bms-cd-info">
                                <div className="bms-cd-name">{c.name} {c.surname}</div>
                                <div className="bms-cd-meta">{c.phone}{c.email ? ` · ${c.email}` : ""}</div>
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  /* New Customer Form */
                  <div className="bms-new-customer-form">
                    <div className="bms-ncf-hint">
                      <TbUserPlus size={14} /> {t("bms_new_customer_hint")}
                    </div>
                    <div className="row g-2">
                      <div className="col-md-6">
                        <Form.Label className="bms-form-label">{t("bms_name")} *</Form.Label>
                        <Form.Control
                          value={customerName}
                          onChange={(e) => setCustomerName(e.target.value)}
                          placeholder={t("bms_name")}
                        />
                      </div>
                      <div className="col-md-6">
                        <Form.Label className="bms-form-label">{t("bms_surname")}</Form.Label>
                        <Form.Control
                          value={customerSurname}
                          onChange={(e) => setCustomerSurname(e.target.value)}
                          placeholder={t("bms_surname")}
                        />
                      </div>
                      <div className="col-md-6">
                        <Form.Label className="bms-form-label">{t("bms_phone")} *</Form.Label>
                        <Form.Control
                          value={customerPhone}
                          onChange={(e) => setCustomerPhone(e.target.value)}
                          placeholder="+90 5XX XXX XX XX"
                        />
                      </div>
                      <div className="col-md-6">
                        <Form.Label className="bms-form-label">{t("bms_email")}</Form.Label>
                        <Form.Control
                          type="email"
                          value={customerEmail}
                          onChange={(e) => setCustomerEmail(e.target.value)}
                          placeholder={t("bms_email")}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="bms-modal-footer">
        <button className="bms-btn-secondary" onClick={onHide}>
          <TbX /> {t("cancel")}
        </button>
        <button
          className="bms-btn-primary"
          onClick={handleCreate}
          disabled={saving || !canSave}
        >
          {saving ? <Spinner size="sm" animation="border" /> : <TbCheck />} {t("save")}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateBookingFromCalendarModal;
